/** @jsx h */
import { h } from 'preact'
import CardStepBoutique from './card-step-boutique'
import CardStepDetails from './card-step-details'
import CardStepInformation from './card-step-information'
import CardStepVisit from './card-step-visit'
import Confirmation from './confirmation'
import Button from './button'
import { useLabel } from '../use-label'
import { useGlobal } from '../use-global'
import useAnalytics from '../use-analytics'
import { createNewAppointment, getTimeSlotsByDateAndType } from '../api'
import ReCAPTCHA from 'react-google-recaptcha'
import { useRef, useState } from 'preact/compat'
import { ABBREVIATION_MAISON, EXTERNAL_POINT_OF_SALE, sha256 } from '../utils'
import clsx from 'clsx'

const AppointmentPage = ({ conf, countries, phoneValidation }) => {
  const recaptchaRef = useRef()
  const [
    {
      purposeMessage,
      selectedBoutique,
      selectedPurpose,
      appointmentDetails,
      personalInfo,
      selectedCountry,
      steps,
      ready,
      canvasConfig,
      serialNumber
    },
    dispatch
  ] = useGlobal()
  const t = useLabel()
  const analytics = useAnalytics()

  const [submitting, setSubmitting] = useState(false)

  const isReadyToSubmit = Object.keys(steps).every(step => steps[step].done && !steps[step].open)

  const setStep = index => dispatch({ type: 'toggle-step', payload: { index } })

  const submitHandler = async () => {
    if (canvasConfig.recaptchaKey && !recaptchaRef.current.getValue()) {
      return false
    }

    try {
      setSubmitting(true)

      if (selectedBoutique.boutiqueType !== EXTERNAL_POINT_OF_SALE) {
        // Check if the timeslot selected is still available
        const timeslots = await getTimeSlotsByDateAndType({
          apiBaseUrl: canvasConfig.apiBaseUrl,
          boutiqueId: selectedBoutique.id,
          date: appointmentDetails.AD_Date,
          type: selectedPurpose.assetType
        })

        const availableSlot = timeslots?.find(el => el.startT.substring(0, 5) === appointmentDetails.AD_Available_Time)

        if (!availableSlot) {
          throw new Error('slot-taken')
        }
      }

      const userCountry = countries.find(
        country => country.iso2Code === personalInfo.PI_Country_Of_Residence
      )?.originalName
      const boutiqueCountry = countries.find(
        country => country.iso2Code === selectedBoutique.countryOfOrigin
      )?.originalName

      const language =
        conf.enabledLanguages
          .find(lang => lang.value === appointmentDetails.AD_Preferred_Language)
          ?.iso2Code?.toLowerCase() || ''

      // If the timeslot is available then proceed with new appointment creation
      await createNewAppointment({
        apiBaseUrl: canvasConfig.apiBaseUrl,
        boutiqueId: selectedBoutique.id,
        body: {
          searchCustomer: {
            firstName: personalInfo.PI_FirstName,
            lastName: personalInfo.PI_LastName,
            email: personalInfo.PI_Email_Address,
            country: personalInfo.PI_Country_Of_Residence,
            maison: ABBREVIATION_MAISON[canvasConfig.maisonId]
          },
          createCustomer: {
            creatingBoutiqueId: selectedBoutique.externalId,
            maison: ABBREVIATION_MAISON[canvasConfig.maisonId],
            countryOfOrigin: boutiqueCountry,
            title: personalInfo.PI_Title,
            gender: personalInfo.PI_Title === '0002' ? 'Male' : 'Female',
            localFirstName: personalInfo.PI_FirstName,
            localLastName: personalInfo.PI_LastName,
            latinFirstName: personalInfo.PI_OtherFirstName,
            latinLastName: personalInfo.PI_OtherLastName,
            spokenLanguage: language,
            writtenLanguage: language,
            partnerCategory: '1',
            fullName: personalInfo.PI_FullName,
            dataOrigin: 'RDV',
            dataSuborigin: 'WEB',
            enforceCustomerPropagation: true,
            electronicContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
            voiceContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
            mmsContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
            socialContact: Boolean(personalInfo.PI_Consent_SendMarketingCom),
            postalContact: false,
            addresses: [
              {
                country: userCountry,
                grMainAddress: true,
                countryOfOrigin: boutiqueCountry,
                addressType: 'BU'
              }
            ],
            emails: [
              {
                emailAddress: personalInfo.PI_Email_Address,
                emailType: '1',
                defaultGrEmail: true,
                countryOfOrigin: boutiqueCountry
              }
            ],
            phones: [
              {
                phoneNumber: personalInfo.PI_Mobile_Number,
                phoneType: 'BU',
                defaultGrPhone: true,
                phoneCountry: personalInfo.PI_Mobile_Prefix?.country,
                countryOfOrigin: boutiqueCountry
              }
            ]
          },
          appointmentData: {
            startDate: appointmentDetails.AD_Date,
            startTime: appointmentDetails.AD_Available_Time
              ? appointmentDetails.AD_Available_Time + ':00.000Z'
              : 'null', // it needs to be null as a string
            purposeOfVisit: selectedPurpose.value || null,
            comments: appointmentDetails.AD_Comments,
            internalComments: canvasConfig.internalComments || null,
            language: appointmentDetails.AD_Preferred_Language,
            phone: personalInfo.PI_Mobile_Prefix?.value + personalInfo.PI_Mobile_Number,
            email: personalInfo.PI_Email_Address,
            isVirtual: appointmentDetails.AD_Appointment_Type === 'VIRTUAL',
            communicationChannel: appointmentDetails.AD_Communication_Channel || null,
            sendAutomaticEmails: true,
            pickUpTypeValue: selectedPurpose.pickUpType,
            customerNationalId: personalInfo.PI_Id_Number || null,
            POVSerialNumber: serialNumber,
            externalRequestTime: appointmentDetails.AD_Preferred_Time || null,
            externalPurposeOfVisit: purposeMessage || null
          }
        }
      }).catch(err => {
        console.log('New appointment creation failed:\n', err)
        throw new Error('new-appointment-failed')
      })

      // Everything went well.
      // Add analytic data and mark ready as true to proceed to confirmation page
      setSubmitting(false)

      dispatch({ type: 'set-ready' })
      analytics.trackConfirmation({
        boutiqueId: selectedBoutique.id,
        selectedCountry,
        purpose: selectedPurpose.value || null,
        boutiqueName: selectedBoutique.commercialName || selectedBoutique.name,
        type: appointmentDetails.AD_Appointment_Type === 'VIRTUAL' ? 'virtual' : 'in_store',
        prefLanguage: appointmentDetails.AD_Preferred_Language,
        channel: appointmentDetails.AD_Communication_Channel || null,
        email: await sha256(personalInfo.PI_Email_Address)
      })
    } catch (err) {
      setSubmitting(false)

      if (err.message === 'slot-taken') {
        dispatch({ type: 'slot-taken' })
        canvasConfig.onSectionExpanded({
          selectorClassName: 'rcc_c_card-step-details',
          index: 2
        })
      }

      // do something if error is new-appointment-failed
      // if (err.message === 'new-appointment-failed') {

      // }

      alert('An error has occurred, please try again.')
    }
  }

  return ready ? (
    <Confirmation />
  ) : (
    <div className='rcc_c_page-appointment rcc-font-widget'>
      <h2
        className={clsx(
          'rcc_c_page-appointment-title rcc-text-[32px] rcc-font-medium rcc-text-center rcc-text-text rcc-uppercase',
          { 'rcc-tracking-wider': canvasConfig.locale !== 'ar' }
        )}
      >
        {t('Global.Request_Appointment')}
      </h2>

      <div className='rcc_c_page-appointment-content'>
        {steps[0].done && !steps[0].open && (
          <h3
            className={clsx(
              'rcc-uppercase rcc-font-normal rcc-text-gray rcc-max-w-[760px] rcc-mx-auto rcc-border-t rcc-border-gray-very-light rcc-py-[18px] mobile:rcc-border-0',
              {
                'rcc-text-right': canvasConfig.direction === 'rtl',
                'rcc-tracking-[1.5px]': canvasConfig.locale !== 'ar'
              }
            )}
          >
            {t('Global.Selected_Appointment')}
          </h3>
        )}
        <CardStepBoutique
          selectorClassName='rcc_c_card-step-boutique'
          conf={conf}
          isOpen={steps[0].open}
          isDone={steps[0].done}
          isEnabled={steps[0].enabled}
          isHighlighted={steps[0].highlighted}
          onToggle={index => {
            if (!steps[0].open) {
              canvasConfig.onSectionExpanded({
                selectorClassName: 'rcc_c_card-step-boutique',
                index
              })
            }
            setStep(index)
          }}
        />
        <CardStepVisit
          selectorClassName='rcc_c_card-step-visit'
          isOpen={steps[1].open}
          isDone={steps[1].done}
          isEnabled={steps[1].enabled}
          isHighlighted={steps[1].highlighted}
          onToggle={index => {
            if (!steps[1].open) {
              canvasConfig.onSectionExpanded({
                selectorClassName: 'rcc_c_card-step-visit',
                index
              })
            }
            setStep(index)
          }}
        />
        <CardStepDetails
          selectorClassName='rcc_c_card-step-details'
          conf={conf}
          isOpen={steps[2].open}
          isDone={steps[2].done}
          isEnabled={steps[2].enabled}
          isHighlighted={steps[2].highlighted}
          onToggle={index => {
            if (!steps[3].open) {
              canvasConfig.onSectionExpanded({
                selectorClassName: 'rcc_c_card-step-details',
                index
              })
            }
            setStep(index)
          }}
        />
        <CardStepInformation
          selectorClassName='rcc_c_card-step-information'
          phoneValidation={phoneValidation}
          countries={countries}
          conf={conf}
          isOpen={steps[3].open}
          isDone={steps[3].done}
          isEnabled={steps[3].enabled}
          isHighlighted={steps[3].highlighted}
          onToggle={index => {
            if (!steps[3].open) {
              canvasConfig.onSectionExpanded({
                selectorClassName: 'rcc_c_card-step-information',
                index
              })
            }
            setStep(index)
          }}
        />

        {isReadyToSubmit && (
          <div className='rcc_c_wrapper-btn-send-request rcc-max-w-[560px] rcc-mx-auto rcc-mt-6'>
            {selectedBoutique.boutiqueType === EXTERNAL_POINT_OF_SALE && (
              <div className='rcc-text-text rcc-text-center rcc-m-8 rcc-font-extralight'>
                {t('Submit.Submit_Data_Sharing_With_External')}
              </div>
            )}
            {canvasConfig.recaptchaKey && (
              <ReCAPTCHA ref={recaptchaRef} sitekey={canvasConfig.recaptchaKey} grecaptcha={window.grecaptcha} />
            )}
            <div className='rcc_c_wrapper-btn-send-request-wrapper'>
              <Button loading={submitting} onClick={submitHandler}>
                {t('Global.Send_Request')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AppointmentPage
